// The bell in the top bar. TWO KINDS OF THING HAVE COME DUE, and it says so in two
// sections rather than one mixed list:
//
//   משימות        — dated work: write the content, place the piece, chase the invoice
//   לידים לחזרה   — contact tracking: we spoke, get back to them today
//
// They are not the same thing and the bell must not imply they are. A task has a title,
// a job and an assignee; a lead follow-up has a person to call and the last thing they
// said. One merged list would have needed a `kind` on every row and a branch in every
// line of markup, to reunite two things that were never one — so the server sends them
// as siblings (`tasks` / `leads`) and they are rendered as siblings.
//
// WHAT THEY DO SHARE is the date rule: both arrive carrying `due_state`, judged once on
// the server by `calculations/dueState` — the same comparison the money screen makes
// about an invoice. Nothing here decides whether a date has arrived; the browser only
// chooses red or amber.
//
// IT COUNTS SCHEDULED THINGS AND NOTHING ELSE. Not open invoices — those carry their own
// mark on כספים. A bell that lit up for every unpaid invoice would never go dark, and a
// notification that is always on is one you stop reading.
//
// IT IS ALSO THE ONLY PLACE SOME TASKS EXIST: a task on a lost lead or a deactivated
// client is on neither board. Those rows say so on their face. A lead FOLLOW-UP has no
// such case — a follow-up on a lost or won lead is not due at all — which is why only
// the task rows carry that marker.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import he from '../i18n/he';
import { useAlerts } from '../context/AlertsContext';
import { boardLink as linkFor, boardLinkLabel as linkLabel } from '../utils/boardLink';
import DateText from './DateText';
import { taskClient } from '../utils/taskClient';

// Board name → where that task is worked, and what the link is called. It lived here
// until היום שלי began reading the very same rows out of the very same payload; it is
// now utils/boardLink, shared by both. Two copies of a route literal is how one screen
// goes on pointing at a page after it moves — which is exactly what the board did when
// it left '/' for the day page.

const isLate = (row) => row.due_state === 'overdue';

// The shared frame: the coloured edge, the client, the state badge and the date. Both
// kinds of row are the same shape on screen — it is the WORDS inside that differ, which
// is the distinction worth carrying and the only one.
function AlertShell({ to, late, client, lead, date, children, foot, onNavigate }) {
  return (
    <Link to={to} className={`alert-row${late ? ' is-overdue' : ''}`} onClick={onNavigate}>
      <span className="alert-head">
        <span className="swatch" style={{ background: client.color || 'var(--muted)' }} />
        <span className="alert-client">
          {/* A lead row names itself: "ליד לחזרה: דוד כהן". A task row is just the
              client — the task's own words are on the line below. */}
          {lead ? `${he.alerts.leadPrefix}: ${client.name}` : client.name}
        </span>
        <span className={`badge ${late ? 'red' : 'amber'}`}>
          {late
            ? (lead ? he.leads.followUpLate : he.alerts.overdue)
            : (lead ? he.leads.followUpDue : he.alerts.dueToday)}
        </span>
        <span className="alert-date"><DateText value={date} /></span>
      </span>
      {children}
      <span className="alert-foot">{foot}</span>
    </Link>
  );
}

function TaskAlertRow({ row, onNavigate }) {
  return (
    <AlertShell
      to={linkFor(row)}
      late={isLate(row)}
      client={taskClient(row.client)}
      date={row.due_date}
      onNavigate={onNavigate}
      foot={(
        <>
          {row.board === 'none'
            ? <span className="alert-orphan" title={he.alerts.noBoardHint}>{he.alerts.noBoard}</span>
            : <span className="muted">{row.assigned_partner_name || ''}</span>}
          <span className="alert-go">{linkLabel(row)}</span>
        </>
      )}
    >
      {/* The note is the message the follow-up was scheduled with — the thing you
          actually need at the moment the date arrives. The title is the fallback, not
          the headline. Opaque: shown, never read. */}
      <span className="alert-text">{row.note || row.title}</span>
    </AlertShell>
  );
}

function LeadAlertRow({ row, onNavigate }) {
  // Always the leads screen. A follow-up lives on the lead, so there is nowhere else it
  // could sensibly take you — no board question and no orphan case.
  return (
    <AlertShell
      to="/leads"
      late={isLate(row)}
      client={row.client}
      lead
      date={row.follow_up_on}
      onNavigate={onNavigate}
      foot={(
        <>
          <span className="muted">{[row.contact_name, row.owner_name].filter(Boolean).join(' · ')}</span>
          <span className="alert-go">{he.alerts.goLead}</span>
        </>
      )}
    >
      {/* WHAT WAS AGREED, read off the last logged conversation. This is the whole reason
          the notes accumulate instead of being one overwritten field: the useful thing at
          the moment you are about to call back is what they said last time. */}
      <span className="alert-text">
        {row.last_note
          ? <>{he.alerts.lastContact} <DateText value={row.last_contacted_on} />: {row.last_note}</>
          : <span className="muted">{he.alerts.noContactYet}</span>}
      </span>
    </AlertShell>
  );
}

// A FILE A CONTENT WRITER SENT IN, that nobody has opened yet (migration 064).
//
// ⚠ IT IS NOT LATE, AND THE ROW SAYS SO BY OMISSION. Every other row in this bell is
// here because a date arrived, and carries a red or amber badge from `due_state`. An
// upload has no date to be late for — it happened. So this row uses the same frame with
// no state badge at all, which is the visual difference the fact deserves: something new
// rather than something overdue.
//
// It links to the JOB, where the file actually is. Opening the file there clears it from
// this list, which is what lets the bell go quiet.
function UploadAlertRow({ row, onNavigate }) {
  return (
    <Link to={`/jobs/${row.jobId}`} className="alert-row" onClick={onNavigate}>
      <span className="alert-head">
        <span className="swatch" style={{ background: row.client.color || 'var(--muted)' }} />
        <span className="alert-client">{row.client.name}</span>
        <span className="badge green">{he.workFiles.unseen}</span>
        <span className="alert-date"><DateText value={row.uploadedAt} /></span>
      </span>
      {/* Who sent what — the two facts that make this actionable. The title is the
          writer's own if they gave one, else the file name. */}
      <span className="alert-text">{he.workFiles.alertLine(row.uploadedBy, row.jobTitle)}</span>
      <span className="alert-foot">
        <span className="muted">{[row.retainerTitle, row.title].filter(Boolean).join(' · ')}</span>
        <span className="alert-go">{he.workFiles.alertGo}</span>
      </span>
    </Link>
  );
}

// A labelled group. Rendered only when it holds something: an empty heading would say
// "there are leads to call" and then show none.
function AlertSection({ title, rows, children }) {
  if (rows.length === 0) return null;
  return (
    <div className="alert-section">
      <div className="alert-section-title">{title}<span className="count">{rows.length}</span></div>
      {children}
    </div>
  );
}

export default function NotificationBell() {
  const alerts = useAlerts();
  const [open, setOpen] = useState(false);
  const { rows: taskRows, counts: taskCounts } = alerts.tasks;
  const { rows: leadRows, counts: leadCounts } = alerts.leads;
  const uploadRows = alerts.uploads.rows;
  const total = alerts.total;
  // Red the moment anything of either kind is late, amber when the day's work is merely
  // waiting. The two never both show: late is the louder state and the count is one
  // number, so the badge answers "should I look" and the panel answers "at what".
  const anyOverdue = taskCounts.overdue > 0 || leadCounts.overdue > 0;
  const level = anyOverdue ? 'overdue' : (total > 0 ? 'today' : null);
  const close = () => setOpen(false);

  return (
    <div className="bell-wrap">
      <button
        type="button"
        className={`bell${level ? ` has-${level}` : ''}`}
        aria-label={he.alerts.bell}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">🔔</span>
        {total > 0 && <span className="bell-count">{total}</span>}
      </button>

      {open && (
        <>
          {/* Closes on any click outside, including on a row's own link. */}
          <div className="bell-scrim" onClick={close} />
          <div className="bell-panel card">
            <div className="bell-panel-head">
              <strong>{he.alerts.title}</strong>
              {total > 0 && (
                <p className="small-note bell-summary">
                  {/* Counted per kind, because "5 things" hides whether that is five
                      pieces of work or five people waiting for a call. */}
                  {[
                    taskCounts.overdue > 0 && ['t-o', 'neg', he.alerts.countOverdue(taskCounts.overdue)],
                    taskCounts.today > 0 && ['t-t', '', he.alerts.countToday(taskCounts.today)],
                    leadCounts.overdue > 0 && ['l-o', 'neg', he.alerts.leadCountOverdue(leadCounts.overdue)],
                    leadCounts.today > 0 && ['l-t', '', he.alerts.leadCountToday(leadCounts.today)],
                  ].filter(Boolean).map(([key, cls, text], i) => (
                    <span key={key}>
                      {i > 0 && <span className="muted"> · </span>}
                      <span className={cls || undefined}>{text}</span>
                    </span>
                  ))}
                </p>
              )}
            </div>

            {total === 0
              ? <p className="muted small-note bell-empty">{he.alerts.empty}</p>
              : (
                <div className="bell-list">
                  <AlertSection title={he.alerts.sectionTasks} rows={taskRows}>
                    {taskRows.map((row) => (
                      <TaskAlertRow key={row.id} row={row} onNavigate={close} />
                    ))}
                  </AlertSection>
                  <AlertSection title={he.alerts.sectionLeads} rows={leadRows}>
                    {leadRows.map((row) => (
                      <LeadAlertRow key={row.key} row={row} onNavigate={close} />
                    ))}
                  </AlertSection>
                  {/* The third kind, and the only one nobody in the office set in
                      motion — it arrived from outside. Its own section for the same
                      reason tasks and leads have theirs: it is a different kind of
                      thing, and merging it would need a discriminator on every row. */}
                  <AlertSection title={he.workFiles.alertTitle} rows={uploadRows}>
                    {uploadRows.map((row) => (
                      <UploadAlertRow key={row.id} row={row} onNavigate={close} />
                    ))}
                  </AlertSection>
                </div>
              )}
          </div>
        </>
      )}
    </div>
  );
}
